import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../api';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { 
  ClipboardList, 
  Trash2, 
  User, 
  Users, 
  Search, 
  Plus, 
  Calendar, 
  FileText,
  Gamepad2,
  Monitor,
  Layers,
  CheckCircle2,
  Pizza,
  RotateCcw
} from 'lucide-react';
import './AssignmentManager.css';

const AssignmentManager = ({ user }) => {
  const { success, error: toastError } = useToast();
  const confirm = useConfirm();
  
  // List states
  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  const [expandedAssignmentId, setExpandedAssignmentId] = useState(null);
  
  // Form states
  const [resourceType, setResourceType] = useState('Quiz');
  const [resources, setResources] = useState([]);
  const [selectedResource, setSelectedResource] = useState('');
  const [loadingResources, setLoadingResources] = useState(false);
  
  const [targetType, setTargetType] = useState('student');
  
  // Target lists
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [department, setDepartment] = useState('waiter');
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [departmentsList, setDepartmentsList] = useState([]);
  
  const [submitting, setSubmitting] = useState(false);
  
  // Filter/Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');

  // Fetch active assignments
  const fetchAssignments = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API_URL}/assignments`, config);
      setAssignments(res.data);
    } catch (err) {
      console.error('Error fetching assignments:', err);
      toastError('Не вдалося завантажити список призначень');
    } finally {
      setLoadingAssignments(false);
    }
  }, [toastError]);

  useEffect(() => {
    const fetchTargets = async () => {
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        
        const [studentsRes, citiesRes, deptsRes] = await Promise.all([
          axios.get(`${API_URL}/students`, config),
          axios.get(`${API_URL}/cities`),
          axios.get(`${API_URL}/departments`)
        ]);
        
        setStudents(studentsRes.data);
        setCities(citiesRes.data);
        setDepartmentsList(deptsRes.data);
        if (deptsRes.data.length > 0) {
          setDepartment(deptsRes.data[0].key);
        }
      } catch (err) {
        console.error('Error fetching target data:', err);
        toastError('Не вдалося завантажити список міст, гравців чи підрозділів');
      }
    };

    fetchAssignments();
    fetchTargets();
  }, [fetchAssignments, toastError]);

  // Fetch resource options dynamically when resourceType changes
  useEffect(() => {
    const fetchResources = async () => {
      setLoadingResources(true);
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        let url = '';
        
        if (resourceType === 'Quiz') url = `${API_URL}/quiz`;
        else if (resourceType === 'GameScenario') url = `${API_URL}/game-scenarios`;
        else if (resourceType === 'DeskTemplate') url = `${API_URL}/templates`;
        else if (resourceType === 'ComplexTest') url = `${API_URL}/complex-tests`;
        else if (resourceType === 'PizzaTemplate') url = `${API_URL}/pizza-templates/templates`;

        if (url) {
          const res = await axios.get(url, config);
          setResources(res.data);
        }
      } catch (err) {
        console.error('Error fetching resources:', err);
        toastError('Не вдалося завантажити список ресурсів');
      } finally {
        setLoadingResources(false);
      }
    };

    fetchResources();
    setSelectedResource('');
  }, [resourceType, toastError]);

  // Create Assignment
  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    if (!selectedResource) {
      toastError('Будь ласка, оберіть ресурс із списку');
      return;
    }
    if (targetType === 'student' && !selectedStudent) {
      toastError('Будь ласка, оберіть гравця');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        targetType,
        resourceType,
        resourceId: selectedResource,
        studentId: targetType === 'student' ? selectedStudent : undefined,
        department: targetType === 'department' ? department : undefined,
        city: targetType === 'department' ? selectedCity : undefined
      };

      await axios.post(`${API_URL}/assignments`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      success('Завдання успішно призначено!');
      fetchAssignments();
      setSelectedResource('');
      setSelectedStudent('');
    } catch (err) {
      console.error('Error creating assignment:', err);
      toastError(err.response?.data?.error || 'Помилка при створенні призначення');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Assignment
  const handleDeleteAssignment = async (id) => {
    const isConfirmed = await confirm({
      title: 'Скасування призначення',
      message: 'Ви впевнені, що хочете видалити це призначення? Гравці більше не бачитимуть його в кабінеті.',
      confirmText: 'Видалити',
      cancelText: 'Скасувати'
    });
    if (!isConfirmed) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/assignments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      success('Призначення успішно скасовано!');
      fetchAssignments();
    } catch (err) {
      console.error('Error deleting assignment:', err);
      toastError(err.response?.data?.error || 'Не вдалося видалити призначення');
    }
  };

  // Reset Assignment completion (Reassign)
  const handleResetAssignment = async (assignmentId, studentId, targetName) => {
    const isAll = !studentId;
    const isConfirmed = await confirm({
      title: 'Скидання проходження',
      message: isAll
        ? `Ви впевнені, що хочете скинути результати для ВСІХ гравців цього призначення? Вони зможуть пройти його знову.`
        : `Ви впевнені, що хочете скинути результат для гравця ${targetName}? Він зможе пройти його знову.`,
      confirmText: 'Скинути',
      cancelText: 'Скасувати'
    });
    if (!isConfirmed) return;

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.patch(
        `${API_URL}/assignments/${assignmentId}/reset`,
        { studentId },
        config
      );
      success(`Результат успішно скинуто для ${targetName}`);
      fetchAssignments();
    } catch (err) {
      console.error('Error resetting assignment:', err);
      toastError(err.response?.data?.error || 'Не вдалося скинути результат');
    }
  };

  // Helpers for displaying info
  const getResourceIcon = (type) => {
    switch (type) {
      case 'Quiz': return <FileText className="res-icon text-purple" size={16} />;
      case 'GameScenario': return <Gamepad2 className="res-icon text-green" size={16} />;
      case 'DeskTemplate': return <Monitor className="res-icon text-yellow" size={16} />;
      case 'ComplexTest': return <Layers className="res-icon text-red" size={16} />;
      case 'PizzaTemplate': return <Pizza className="res-icon text-orange" size={16} />;
      default: return <ClipboardList className="res-icon" size={16} />;
    }
  };

  const getResourceTypeLabel = (type) => {
    switch (type) {
      case 'Quiz': return 'Квіз';
      case 'GameScenario': return 'Гра-сценарій';
      case 'DeskTemplate': return 'Сервірування';
      case 'ComplexTest': return 'Комплексний тест';
      case 'PizzaTemplate': return 'Конструктор піци';
      default: return type;
    }
  };

  const getDepartmentLabel = (deptKey) => {
    const found = departmentsList.find(d => d.key === deptKey);
    return found ? found.name : deptKey;
  };

  // Filter and search
  const filteredAssignments = assignments.filter((ass) => {
    const matchesType = typeFilter === 'All' || ass.resourceType === typeFilter;
    
    let matchesQuery = false;
    const titleMatch = ass.title?.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (ass.targetType === 'student' && ass.studentId) {
      const studentName = `${ass.studentId.studentLastName} ${ass.studentId.studentName}`.toLowerCase();
      matchesQuery = studentName.includes(searchQuery.toLowerCase()) || titleMatch;
    } else if (ass.targetType === 'department') {
      const deptName = getDepartmentLabel(ass.department).toLowerCase();
      const cityName = (ass.city || 'усі міста').toLowerCase();
      matchesQuery = deptName.includes(searchQuery.toLowerCase()) || cityName.includes(searchQuery.toLowerCase()) || titleMatch;
    } else {
      matchesQuery = titleMatch;
    }

    return matchesType && matchesQuery;
  });

  return (
    <div className="assignment-manager">
      <div className="content-header">
        <div>
          <h2>🎯 Керування призначеннями</h2>
          <p className="subtitle">Призначайте навчальні модулі та контролюйте прогрес їх виконання</p>
        </div>
      </div>

      <div className="am-grid">
        {/* Form Card */}
        <div className="am-card am-form-card">
          <h3>📌 Нове призначення</h3>
          <form onSubmit={handleCreateAssignment} className="am-form">
            
            <div className="form-group">
              <label>Тип ресурсу</label>
              <div className="resource-type-cards">
                <div 
                  className={`rt-card ${resourceType === 'Quiz' ? 'active' : ''}`}
                  onClick={() => setResourceType('Quiz')}
                >
                  <FileText className="rt-icon text-purple" size={18} />
                  <span className="rt-label">📝 Квізи</span>
                </div>
                <div 
                  className={`rt-card ${resourceType === 'GameScenario' ? 'active' : ''}`}
                  onClick={() => setResourceType('GameScenario')}
                >
                  <Gamepad2 className="rt-icon text-green" size={18} />
                  <span className="rt-label">🎮 Ігри</span>
                </div>
                <div 
                  className={`rt-card ${resourceType === 'DeskTemplate' ? 'active' : ''}`}
                  onClick={() => setResourceType('DeskTemplate')}
                >
                  <Monitor className="rt-icon text-yellow" size={18} />
                  <span className="rt-label">🍽️ Столи</span>
                </div>
                <div 
                  className={`rt-card ${resourceType === 'ComplexTest' ? 'active' : ''}`}
                  onClick={() => setResourceType('ComplexTest')}
                >
                  <Layers className="rt-icon text-red" size={18} />
                  <span className="rt-label">📚 Комплексні</span>
                </div>
                <div 
                  className={`rt-card ${resourceType === 'PizzaTemplate' ? 'active' : ''}`}
                  onClick={() => setResourceType('PizzaTemplate')}
                >
                  <Pizza className="rt-icon text-orange" size={18} />
                  <span className="rt-label">🍕 Піца-пари</span>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="resource-select">Оберіть конкретний тест/гру</label>
              {loadingResources ? (
                <div className="am-field-loading">Завантаження варіантів...</div>
              ) : (
                <select
                  id="resource-select"
                  value={selectedResource}
                  onChange={(e) => setSelectedResource(e.target.value)}
                  required
                >
                  <option value="">-- Оберіть зі списку --</option>
                  {resources.map((res) => (
                    <option key={res._id} value={res._id}>
                      {res.title || res.name || res.templateName || 'Без назви'}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="divider" />

            <div className="form-group">
              <label>Кому призначити</label>
              <div className="target-toggle">
                <button
                  type="button"
                  className={`toggle-btn ${targetType === 'student' ? 'active' : ''}`}
                  onClick={() => setTargetType('student')}
                >
                  <User size={16} />
                  <span>Гравець</span>
                </button>
                <button
                  type="button"
                  className={`toggle-btn ${targetType === 'department' ? 'active' : ''}`}
                  onClick={() => setTargetType('department')}
                >
                  <Users size={16} />
                  <span>Посада (Група)</span>
                </button>
              </div>
            </div>

            {targetType === 'student' ? (
              <div className="form-group">
                <label htmlFor="student-select">Оберіть гравця</label>
                <select
                  id="student-select"
                  value={selectedStudent}
                  onChange={(e) => setSelectedStudent(e.target.value)}
                  required
                >
                  <option value="">-- Оберіть гравця --</option>
                  {students.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.studentLastName} {s.studentName} ({s.studentCity || 'не вказано'}, {s.position || 'без посади'})
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="department-row">
                <div className="form-group flex-1">
                  <label htmlFor="department-select">Посада</label>
                  <select
                    id="department-select"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    required
                  >
                    {departmentsList.length === 0 ? (
                      <>
                        <option value="waiter">🧑‍🍳 Офіціанти</option>
                        <option value="banquet_manager">🥂 Банкетні менеджери</option>
                        <option value="administrator">💼 Адміністратори</option>
                      </>
                    ) : (
                      departmentsList.map(d => (
                        <option key={d._id} value={d.key}>{d.name}</option>
                      ))
                    )}
                  </select>
                </div>
                <div className="form-group flex-1">
                  <label htmlFor="city-select">Місто</label>
                  <select
                    id="city-select"
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                  >
                    <option value="">📍 Усі міста</option>
                    {cities.map((c) => (
                      <option key={c._id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <button type="submit" className="btn-primary btn-submit" disabled={submitting}>
              {submitting ? 'Створення...' : (
                <>
                  <Plus size={18} />
                  <span>Призначити завдання</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* List Card */}
        <div className="am-card am-list-card">
          <div className="card-header-flex">
            <h3>📋 Активні призначення ({filteredAssignments.length})</h3>
            <div className="filters-container">
              <div className="search-wrapper">
                <Search size={16} className="search-icon" />
                <input
                  type="text"
                  placeholder="Пошук..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="type-filter-select"
              >
                <option value="All">Усі типи</option>
                <option value="Quiz">Квізи</option>
                <option value="GameScenario">Ігри</option>
                <option value="DeskTemplate">Сервірування</option>
                <option value="ComplexTest">Комплексні</option>
                <option value="PizzaTemplate">Піци</option>
              </select>
            </div>
          </div>

          <div className="table-responsive">
            {loadingAssignments ? (
              <div className="list-loading">Завантаження призначень...</div>
            ) : filteredAssignments.length === 0 ? (
              <div className="list-empty">Активних призначень не знайдено</div>
            ) : (
              <table className="am-table">
                <thead>
                  <tr>
                    <th>Ресурс</th>
                    <th>Тип</th>
                    <th>Кому призначено</th>
                    <th>Створено</th>
                    <th>Виконано</th>
                    <th>Дія</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAssignments.map((ass) => (
                    <tr key={ass._id}>
                      <td className="td-title" title={ass.title}>
                        <div className="title-wrapper">
                          {getResourceIcon(ass.resourceType)}
                          <span className="res-title-text">{ass.title}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`type-badge ${ass.resourceType.toLowerCase().replace('scenario', '').replace('template', '')}`}>
                          {getResourceTypeLabel(ass.resourceType)}
                        </span>
                      </td>
                      <td className="td-target">
                        {ass.targetType === 'student' ? (
                          ass.studentId ? (
                            <div className="target-wrapper student">
                              <User size={14} />
                              <span>{ass.studentId.studentLastName} {ass.studentId.studentName}</span>
                              <span className="target-tag student-city">{ass.studentId.studentCity}</span>
                            </div>
                          ) : (
                            <span className="text-muted">Видалений гравець</span>
                          )
                        ) : (
                          <div className="target-wrapper dept">
                            <Users size={14} />
                            <span>{getDepartmentLabel(ass.department)}</span>
                            <span className="target-tag dept-city">
                              {ass.city ? `📍 ${ass.city}` : '🌐 Усі міста'}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="td-date">
                        <div className="date-wrapper">
                          <Calendar size={14} />
                          <span>{new Date(ass.createdAt).toLocaleDateString('uk-UA')}</span>
                        </div>
                      </td>
                      <td>
                        <div className="progress-wrapper">
                          <button
                            type="button"
                            className={`am-progress-badge clickable ${expandedAssignmentId === ass._id ? 'active' : ''}`}
                            onClick={() => setExpandedAssignmentId(expandedAssignmentId === ass._id ? null : ass._id)}
                            title="Переглянути список тих, хто пройшов"
                          >
                            <CheckCircle2 size={12} />
                            <span>{ass.completedPlayers?.length || 0} пройшли</span>
                          </button>
                          
                          {expandedAssignmentId === ass._id && (
                            <div className="completed-players-popup">
                              <div className="popup-header">
                                <h4>Пройшли завдання:</h4>
                                <button 
                                  type="button"
                                  className="popup-close-btn" 
                                  onClick={(e) => { e.stopPropagation(); setExpandedAssignmentId(null); }}
                                >
                                  ×
                                </button>
                              </div>
                              {(!ass.completedPlayers || ass.completedPlayers.length === 0) ? (
                                <div className="popup-empty">Ще ніхто не пройшов</div>
                              ) : (
                                <>
                                  <div className="popup-list">
                                    {ass.completedPlayers.map((player, idx) => {
                                      const s = player.studentId;
                                      const sName = s 
                                        ? `${s.studentLastName} ${s.studentName}`
                                        : 'Видалений гравець';
                                      const sCity = s?.studentCity || '';
                                      const sPos = s?.position ? `(${s.position})` : '';
                                      return (
                                        <div key={player._id || idx} className="popup-item">
                                          <div className="popup-student-info">
                                            <span className="popup-student-name">{sName}</span>
                                            {(sCity || sPos) && (
                                              <span className="popup-student-city">
                                                {sCity} {sPos}
                                              </span>
                                            )}
                                          </div>
                                          <div className="popup-student-meta" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0.4rem' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                              <span className="popup-student-score">{player.percentage}%</span>
                                              <span className="popup-student-date">
                                                {new Date(player.completedAt).toLocaleDateString('uk-UA')}
                                              </span>
                                            </div>
                                            {s && (
                                              <button
                                                type="button"
                                                className="btn-reset-player"
                                                onClick={() => handleResetAssignment(ass._id, s._id, sName)}
                                                title="Скинути результат для цього гравця"
                                              >
                                                <RotateCcw size={14} />
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  <div className="popup-footer">
                                    <button
                                      type="button"
                                      className="btn-reset-all"
                                      onClick={() => handleResetAssignment(ass._id, null, 'всіх гравців')}
                                    >
                                      Скинути для всіх
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="td-actions">
                        <button
                          type="button"
                          className="btn-delete"
                          onClick={() => handleDeleteAssignment(ass._id)}
                          title="Скасувати призначення"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignmentManager;
